// 八字 AI 求测服务：经 Cloud Functions 调用（baziSessions 集合没有客户端规则，
// 因此历史读写也走 Functions 端点，由 Admin SDK 落库）。
import { type User } from 'firebase/auth';
import { computeNowLayer } from '../features/bazi/engine.js';

export interface BaziChatMessage {
  role: 'user' | 'assistant';
  content: any;                 // user: string；assistant: 解读/问答 JSON
  mode?: 'reading' | 'ask';
  ts?: number;
  error?: string;               // 仅失败轮携带
}

export interface BaziReadingData {
  overview?: string;
  personality?: string;
  career?: string;
  wealth?: string;
  love?: string;
  health?: string;
  family?: string;
  luckCycle?: string;
  summary?: string;
  disclaimer?: string;
  analysis?: string;            // 问答模式（旧版整段）
  // —— 问答模式（新版结构化：直答 → 白话现况 → 依据 → 做/避 → 应期） ——
  domain?: string;              // 服务端按问题命中的事类（如「事业官禄」）
  answer?: string;              // 直答一句话
  situation?: string;           // 白话现状解读
  reason?: string;              // 精简盘面依据（默认折叠）
  advice?: string[];            // 「做：」「避：」条目
  timing?: string;              // 应期（具体年份；无动应时明说）
}

export interface BaziSessionSummary {
  id: string;
  title: string;
  solarText: string;
  gender: string;
  name: string;
  city: string;
  messagesCount: number;
  updatedAt: string;
}

export interface BaziSessionFull {
  id: string;
  title?: string;
  chart: any;
  messages: BaziChatMessage[];
  createdAt?: string;
  updatedAt?: string;
}

export const BAZI_API_BASE: string =
  ((import.meta as any).env?.VITE_API_BASE as string | undefined)?.replace(/\/+$/, '') ||
  'https://us-central1-meihua-16427.cloudfunctions.net';

async function apiFetch(path: string, init: RequestInit & { token: string }): Promise<any> {
  const response = await fetch(`${BAZI_API_BASE}/api${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${init.token}` }
  });
  let data: any = {};
  try {
    data = await response.json();
  } catch {
    data = {};
  }
  if (!response.ok) {
    const raw =
      (typeof data?.message === 'string' && data.message) ||
      (typeof data?.error === 'string' && data.error) ||
      '';
    let message = raw;
    if (/service configuration error/i.test(raw)) {
      message = 'AI 服务尚未配置完成，请稍后再试或联系管理员（排盘功能不受影响）。';
    } else if (response.status === 429) {
      message = '请求太频繁，请稍等片刻再试。';
    } else if (response.status === 402) {
      message = raw || 'AI 次数已用完，请到「我的 → 订阅计划」查看。';
    } else if (!raw) {
      message = `请求失败（${response.status}）`;
    }
    const err = new Error(message) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }
  return data;
}

const tokenOf = async (user: User | null): Promise<string> => {
  if (!user) throw new Error('未登录');
  return user.getIdToken();
};

/**
 * 给服务端瘦身命盘：剔除后端提示词用不到的原始重字段（其它年份的逐日流年、
 * relations 的长篇 meaning、strength.detail 明细），当前大运的近两年流年保留。
 * 服务端 12000 字符上限由此不再误伤正常盘。
 */
function slimChartForApi(chart: any): any {
  if (!chart || typeof chart !== 'object') return chart;
  const c = JSON.parse(JSON.stringify(chart));
  const y = new Date().getFullYear();
  if (Array.isArray(c.dayun)) {
    c.dayun.forEach((d: any) => {
      if (d && d.startYear <= y && y <= d.endYear) {
        d.liunian = (d.liunian || []).filter((l: any) => Math.abs((l.year || 0) - y) <= 1);
      } else {
        delete d.liunian;
      }
    });
  }
  if (Array.isArray(c.relations)) {
    c.relations.forEach((r: any) => { if (r) delete r.meaning; });
  }
  if (c.strength) delete c.strength.detail;
  // 「当下」时间层（流年/流月/流日）按发请求的时刻重算：命盘可能来自缓存或
  // 历史会话快照，直接沿用其中的干支会把过期时间层带给 AI。
  try {
    const fresh = computeNowLayer(c.dayGan);
    if (fresh) c.now = fresh;
  } catch { /* 保留盘面已有的 now 层（若有） */ }
  return c;
}

/** 调用 /api/ai/bazi-ask：reading＝八小节解读；ask＝自由问答（history 携带此前对话，chart 每轮随行） */
export async function askBazi(
  user: User | null,
  payload: { mode: 'reading' | 'ask'; question?: string; chart: any; history?: BaziChatMessage[]; sessionId?: string | null }
): Promise<{ data: BaziReadingData; sessionId: string | null; title: string; model?: string }> {
  const token = await tokenOf(user);
  const res = await apiFetch('/ai/bazi-ask', {
    method: 'POST',
    token,
    body: JSON.stringify({ ...payload, chart: slimChartForApi(payload.chart) })
  });
  return {
    data: res?.data ?? {},
    sessionId: res?.metadata?.sessionId ?? null,
    title: res?.metadata?.title ?? '',
    model: res?.metadata?.model
  };
}

/** 八字求测历史列表（最近 50 条） */
export async function listBaziSessions(user: User | null): Promise<BaziSessionSummary[]> {
  if (!user) return [];
  const token = await tokenOf(user);
  const res = await apiFetch(`/web/bazi-history/${user.uid}`, { method: 'GET', token });
  return (res?.data ?? []) as BaziSessionSummary[];
}

/** 取完整会话（chart 快照 + 全部消息） */
export async function getBaziSession(user: User | null, sessionId: string): Promise<BaziSessionFull | null> {
  if (!user) return null;
  const token = await tokenOf(user);
  try {
    const res = await apiFetch(`/web/bazi-session/${user.uid}/${sessionId}`, { method: 'GET', token });
    return (res?.data ?? null) as BaziSessionFull | null;
  } catch (err: any) {
    if (err?.status === 404) return null;
    throw err;
  }
}

/** 删除一条八字求测会话 */
export async function deleteBaziSession(user: User | null, sessionId: string): Promise<void> {
  if (!user) return;
  const token = await tokenOf(user);
  await apiFetch(`/web/bazi-session/${user.uid}/${sessionId}`, { method: 'DELETE', token });
}

/** 历史页「去八字页继续」：把会话快照写入交接缓存，由 BaziPage 在挂载时消费 */
export function stashBaziRestoreSession(session: BaziSessionFull): void {
  try {
    localStorage.setItem('bz-ai-restore', JSON.stringify({ chart: session.chart, ts: Date.now() }));
  } catch {
    /* 存储满等失败不阻塞跳转 */
  }
}
